import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'

/* The one place GSAP is configured and the only place tweens are authored.
   Components call these helpers; they never write timelines inline.

   House style, per the animation law: slow, smooth, intentional. Nothing
   bounces, nothing rotates, nothing runs faster than 0.6s.

   The choreography has four registers, and every helper belongs to one:

     arrival   — plays once, right now: the hero, a page masthead, a dialog
     reveal    — plays once, when scrolled to: headings, grids, photographs
     scrub     — moves exactly as far as the visitor scrolls, and stops when
                 they do: parallax, progress rails, ghost lettering
     response  — answers the pointer: magnetic buttons, the card spotlight

   A reveal never fires under a page curtain. The curtain is the only thing
   on the site that runs *before* content, and it says how long it needs
   through entryDelay(), which every arrival and reveal adds to its own. */

export const EASE = 'power3.out'
export const EASE_INOUT = 'power2.inOut'

export const DURATION = {
  fast: 0.6,
  base: 0.9,
  slow: 1.2,
  hero: 1.6,
} as const

/* The masked-line rise: a line starts this far below its own mask. 116%
   rather than 100% so the descenders clear the mask edge. */
const LINE_RISE = 116

let registered = false

/** Registers the plugins exactly once. Called from main.tsx. */
export function registerGsap() {
  if (registered) return
  gsap.registerPlugin(ScrollTrigger, SplitText, DrawSVGPlugin)
  gsap.defaults({ ease: EASE, duration: DURATION.base })
  registered = true
}

/** True when the visitor has asked the OS for less motion. */
export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/* True when this page is the console's preview frame — the same test
   CmsProvider makes, read once at module load so it is settled before the
   first animation is built.

   Read at load rather than off the class CmsProvider puts on <html>, because
   effects run child-first: a section's animation is created before the
   provider's own effect has run. */
const IN_EDITOR =
  typeof window !== 'undefined' &&
  window.parent !== window &&
  new URLSearchParams(window.location.search).get('cms') === '1'

/** True when an animation should not be built at all, leaving the DOM in its
    natural, finished state.

    Two reasons, and they want the same thing. Somebody has asked the OS for
    less motion — or this is the page builder's preview, where a person is
    rewriting a headline and needs to see the words, not a reveal. Skipping is
    what makes the live preview live in both senses: nothing is left waiting
    below the fold at opacity zero, and — the reason it matters most —
    SplitText never rewrites a heading into a pile of masked line divs that
    React can no longer put new text into. */
function skipMotion() {
  return IN_EDITOR || prefersReducedMotion()
}

/** Fine pointers only: a thumb cannot hover, so nothing should chase it. */
function hasFinePointer() {
  return window.matchMedia('(pointer: fine)').matches
}

/* ---------- the page curtain clock ----------

   PageTransition drops a curtain over a freshly mounted page and lifts it
   over the next few hundred milliseconds. Anything that arrives above the
   fold on that page would otherwise play its entrance underneath it and be
   finished by the time it is uncovered. So the curtain leaves a timestamp,
   and every arrival and reveal asks how long is left. */

let curtainUntil = 0

/** Called by PageTransition the moment a route changes. */
export function armCurtain(ms: number) {
  curtainUntil = performance.now() + ms
}

/** Seconds a tween created right now should wait for the curtain. */
export function entryDelay() {
  return Math.max(0, curtainUntil - performance.now()) / 1000
}

/* Starts a paused timeline after a wait. Not `.delay(s).play()`: play()
   re-aligns a paused animation's start time to keep its playhead where it
   is, which quietly discards the delay. */
function playAfter(timeline: gsap.core.Timeline, seconds: number) {
  if (seconds > 0) gsap.delayedCall(seconds, () => timeline.play())
  else timeline.play()
}

/* ---------- masked headlines and font loading ----------

   SplitText with autoSplit re-splits a headline when the web fonts finish
   loading and whenever its width changes. Before it does, it reverts the
   animation the previous onSplit returned, then calls onSplit again and
   restores the new animation's playhead to where the old one was. So a
   masked-line reveal is always built inside onSplit, always returned, and
   never guarded with a "played" flag: the guard is what left the first
   version of the hero dead two hundred milliseconds in, when Sora landed
   and the lines were re-cut. */

type Target = gsap.TweenTarget

type RevealOptions = {
  /** Element whose entry starts the tween. Defaults to the target itself. */
  trigger?: Element
  x?: number
  y?: number
  scale?: number
  delay?: number
  duration?: number
  start?: string
}

/* Every helper below returns early under reduced motion, leaving the DOM in
   its natural, finished state — nothing is left invisible. */

/** Fade and rise a section into view, once. */
export function revealOnScroll(target: Target, options: RevealOptions = {}) {
  if (skipMotion()) return

  const {
    trigger,
    x = 0,
    y = 24,
    scale,
    delay = 0,
    duration = DURATION.base,
    start = 'top 80%',
  } = options

  /* Hidden now, revealed when scrolled to. Built as set-then-to rather than
     a from-tween so the delay can be read when the trigger fires — the
     curtain may be down when this is created and long gone by the time the
     element is reached. */
  gsap.set(target, { opacity: 0, x, y, ...(scale ? { scale } : {}) })

  return ScrollTrigger.create({
    trigger: trigger ?? (target as Element),
    start,
    once: true,
    onEnter: () =>
      gsap.to(target, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        delay: delay + entryDelay(),
        duration,
        ease: EASE,
        overwrite: 'auto',
        clearProps: 'transform',
      }),
  })
}

type StaggerOptions = RevealOptions & { stagger?: number }

/* Reveal a row or grid of children in sequence, once.

   Each child is its own trigger, batched: whatever enters the viewport in
   the same beat rises together, and a card twenty rows down waits for its
   own arrival instead of playing, unseen, when the top of the grid did.
   That is the difference between a stagger that reads on a phone — where a
   six-up grid is a single column three screens tall — and one that does
   not. */
export function staggerChildren(
  container: Element,
  selector: string,
  options: StaggerOptions = {},
) {
  const targets = Array.from(container.querySelectorAll(selector))
  if (!targets.length || skipMotion()) return

  const {
    x = 0,
    y = 28,
    scale,
    delay = 0,
    duration = DURATION.base,
    start = 'top 88%',
    stagger = 0.08,
  } = options

  gsap.set(targets, { opacity: 0, x, y, ...(scale ? { scale } : {}) })

  return ScrollTrigger.batch(targets, {
    start,
    once: true,
    batchMax: 12,
    interval: 0.1,
    onEnter: (batch) =>
      gsap.to(batch, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        delay: delay + entryDelay(),
        duration,
        stagger,
        ease: EASE,
        overwrite: 'auto',
        clearProps: 'transform',
      }),
  })
}

/* ---------- the hero ----------

   Reads data-hero attributes rather than class names, so markup can be
   restyled freely without breaking the animation. The roles:

     light   the ambient lime pools — fade up first, the room's lights
     ring    the neon arc's bright stroke — draws itself clockwise from
             twelve o'clock, the sign switching on
     glow    everything the ring illuminates — the wide halo, the floor
             pool, the light thrown back onto the car
     image   the plate the car stands on — settles from an overscale
     title   the headline — masked lines rising as the ring completes
     line    supporting copy — fade-and-rise, in reading order
     fade    anything a CSS animation already moves — opacity only, since
             two owners of one transform fight
     strip   the assurance cells — one by one along the foot
     arrow   the hand-drawn arrow — drawn, not faded
     count   a figure that runs up to its value

   The stroke drawing and the title share a beat on purpose: the ring
   finishes lighting as the last line lands, so the section reads as one
   movement rather than a checklist of parts.

   Returns a teardown, because SplitText rewrites the headline's markup and
   gsap.context only reverts tweens. */
export function heroIntro(scope: Element) {
  if (skipMotion()) {
    scope.querySelectorAll<HTMLElement>('[data-hero="count"]').forEach(writeFinal)
    return
  }

  const q = <T extends Element = Element>(role: string) =>
    Array.from(scope.querySelectorAll<T>(`[data-hero="${role}"]`))

  const lights = q('light')
  const ring = q('ring')
  const glow = q('glow')
  const image = q('image')
  const title = scope.querySelector<HTMLElement>('[data-hero="title"]')
  const lines = q('line')
  const fades = q('fade')
  const strip = q('strip')
  const arrow = q('arrow')
  const counts = q<HTMLElement>('count')

  /* Rebuilt in full on every split — see the note on font loading above —
     and handed back so SplitText can carry the playhead across. */
  const build = (titleLines: Element[]) => {
    const timeline = gsap.timeline({
      defaults: { ease: EASE },
      delay: entryDelay(),
    })

    /* Every tween states both ends. A from() reads its destination off the
       element, and after a revert mid-stagger that reading can be the
       hidden state it was meant to leave — see the note on font loading. */
    if (lights.length) {
      timeline.fromTo(
        lights,
        { opacity: 0 },
        { opacity: 1, duration: DURATION.hero },
        0,
      )
    }

    if (ring.length) {
      timeline.fromTo(
        ring,
        { drawSVG: '0%' },
        { drawSVG: '0% 100%', duration: 1.5, ease: EASE_INOUT },
        0.1,
      )
    }

    if (image.length) {
      timeline.fromTo(
        image,
        { opacity: 0, scale: 1.08 },
        { opacity: 1, scale: 1, duration: DURATION.hero, ease: EASE_INOUT },
        0.3,
      )
    }

    if (glow.length) {
      timeline.fromTo(
        glow,
        { opacity: 0 },
        { opacity: 1, duration: DURATION.slow },
        0.7,
      )
    }

    if (titleLines.length) {
      timeline.fromTo(
        titleLines,
        { yPercent: LINE_RISE },
        { yPercent: 0, duration: DURATION.slow, stagger: 0.11 },
        0.45,
      )
    }

    if (lines.length) {
      timeline.fromTo(
        lines,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: DURATION.base, stagger: 0.1 },
        0.85,
      )
    }

    if (fades.length) {
      timeline.fromTo(
        fades,
        { opacity: 0 },
        { opacity: 1, duration: DURATION.base, stagger: 0.15 },
        1.2,
      )
    }

    if (strip.length) {
      timeline.fromTo(
        strip,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: DURATION.base, stagger: 0.07 },
        1.3,
      )
    }

    if (arrow.length) {
      timeline.fromTo(
        arrow,
        { drawSVG: '0%' },
        { drawSVG: '0% 100%', duration: DURATION.base, ease: EASE_INOUT },
        1.5,
      )
    }

    counts.forEach((el) => {
      const tween = countUp(el, Number(el.dataset.count), {
        suffix: el.dataset.suffix ?? '',
        immediate: true,
        duration: 1.4,
      })
      if (tween) timeline.add(tween, 1.0)
    })

    return timeline
  }

  if (!title) {
    build([])
    return
  }

  const split = SplitText.create(title, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'split-line',
    autoSplit: true,
    aria: 'auto',
    onSplit: (self) => build(self.lines),
  })

  return () => split.revert()
}

/* The hero leaving. As the visitor scrolls past it the copy lifts away a
   little faster than the page and the stage lags a little behind it — two
   planes, the depth a photograph of a car has and a flat page does not.
   Scrubbed, so it is the scroll itself, not a tween. Desktop only: on a
   phone the hero is two screens tall and its parts leave one at a time
   anyway. */
export function heroExit(scope: Element) {
  if (skipMotion()) return
  if (!window.matchMedia('(min-width: 1024px)').matches) return

  const copy = scope.querySelector('[data-hero="copy"]')
  const stage = scope.querySelector('[data-hero="stage"]')
  if (!copy && !stage) return

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: scope,
      start: 'top top',
      end: 'bottom top',
      scrub: 0.6,
    },
  })

  if (copy) timeline.to(copy, { y: -70, opacity: 0.25, ease: 'none' }, 0)
  if (stage) timeline.to(stage, { y: 90, ease: 'none' }, 0)

  return timeline
}

/* ---------- a section heading ----------

   Every section opens the same way, so they all reveal the same way. Reads
   data-heading roles:

     rule     the coachline beside the eyebrow — drawn from its near end
     eyebrow  the label — a plain fade under the line being drawn
     title    the headline — masked lines rising
     body     the lede and the action — fade-and-rise after the title

   One trigger for the whole block, so the rule, the label and the first
   line of the title land on the same beat. Returns a teardown for the
   SplitText markup. */
export function headingReveal(scope: Element, options: { start?: string } = {}) {
  if (skipMotion()) return

  const { start = 'top 85%' } = options

  const rules = scope.querySelectorAll('[data-heading="rule"]')
  const eyebrow = scope.querySelector('[data-heading="eyebrow"]')
  const title = scope.querySelector<HTMLElement>('[data-heading="title"]')
  const body = scope.querySelectorAll('[data-heading="body"]')

  /* The whole block is one paused timeline, rebuilt on every split and
     handed back to SplitText. One trigger plays whichever build is current;
     a build made after that plays itself, and SplitText puts its playhead
     where the previous one's was. */
  let played = false
  let current: gsap.core.Timeline | undefined

  const build = (lines: Element[]) => {
    const timeline = gsap.timeline({ paused: true, defaults: { ease: EASE } })

    if (rules.length) {
      timeline.fromTo(
        rules,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: DURATION.base,
          ease: EASE_INOUT,
          transformOrigin: (index, el: Element) =>
            el.matches(':last-child') && index > 0 ? 'right center' : 'left center',
        },
        0,
      )
    }

    if (eyebrow) {
      timeline.fromTo(
        eyebrow,
        { opacity: 0 },
        { opacity: 1, duration: DURATION.fast },
        0,
      )
    }

    if (lines.length) {
      timeline.fromTo(
        lines,
        { yPercent: LINE_RISE },
        { yPercent: 0, duration: DURATION.slow, stagger: 0.1 },
        0.15,
      )
    }

    if (body.length) {
      timeline.fromTo(
        body,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: DURATION.base, stagger: 0.1 },
        0.4,
      )
    }

    if (played) timeline.play()
    current = timeline
    return timeline
  }

  ScrollTrigger.create({
    trigger: scope,
    start,
    once: true,
    onEnter: () => {
      played = true
      if (current) playAfter(current, entryDelay())
    },
  })

  if (!title) {
    build([])
    return
  }

  const split = SplitText.create(title, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'split-line',
    autoSplit: true,
    aria: 'auto',
    onSplit: (self) => build(self.lines),
  })

  return () => split.revert()
}

/* ---------- a vehicle's page ----------

   The detail page arrives in the order somebody looks at a car: the way
   back and the name first, then the photograph, then the money. Reads
   data-detail roles:

     crumb    the breadcrumb row — drops in from just above
     eyebrow  marque and tag
     title    the car's name — masked lines
     stage    the photographs — the shutter rises and the frame settles
     deal     the glass panel — slides in from the right edge it hangs on
     facts    the ribbon of facts — cell by cell
     section  everything below — revealed as it is scrolled to

   Returns a teardown for the SplitText markup. */
export function detailIntro(scope: Element) {
  if (skipMotion()) return

  const one = <T extends Element = Element>(role: string) =>
    scope.querySelector<T>(`[data-detail="${role}"]`)

  const crumb = one('crumb')
  const eyebrow = one('eyebrow')
  const title = one<HTMLElement>('title')
  const stage = one('stage')
  const deal = one('deal')
  const facts = one('facts')

  /* Rebuilt in full on every split and handed back, like the hero. */
  const build = (lines: Element[]) => {
    const timeline = gsap.timeline({
      defaults: { ease: EASE },
      delay: entryDelay(),
    })

    if (crumb) {
      timeline.fromTo(
        crumb,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: DURATION.fast },
        0,
      )
    }

    if (eyebrow) {
      timeline.fromTo(
        eyebrow,
        { opacity: 0 },
        { opacity: 1, duration: DURATION.fast },
        0.1,
      )
    }

    if (lines.length) {
      timeline.fromTo(
        lines,
        { yPercent: LINE_RISE },
        { yPercent: 0, duration: DURATION.slow, stagger: 0.1 },
        0.15,
      )
    }

    if (stage) {
      timeline.fromTo(
        stage,
        { clipPath: 'inset(100% 0% 0% 0%)' },
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: DURATION.slow,
          ease: EASE_INOUT,
          clearProps: 'clipPath',
        },
        0.35,
      )
      const picture = stage.querySelector('img')
      if (picture) {
        timeline.fromTo(
          picture,
          { scale: 1.15 },
          { scale: 1, duration: DURATION.slow * 1.25, clearProps: 'transform' },
          0.35,
        )
      }
    }

    if (deal) {
      timeline.fromTo(
        deal,
        { opacity: 0, x: 36 },
        { opacity: 1, x: 0, duration: DURATION.slow, clearProps: 'transform' },
        0.5,
      )
    }

    if (facts?.children.length) {
      timeline.fromTo(
        facts.children,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: DURATION.base, stagger: 0.06 },
        0.8,
      )
    }

    return timeline
  }

  staggerChildren(scope, '[data-detail="section"]', { y: 24, stagger: 0.08 })

  if (!title) {
    build([])
    return
  }

  const split = SplitText.create(title, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'split-line',
    autoSplit: true,
    aria: 'auto',
    onSplit: (self) => build(self.lines),
  })

  return () => split.revert()
}

type CountUpOptions = {
  duration?: number
  decimals?: number
  prefix?: string
  suffix?: string
  start?: string
  /** Run now rather than when scrolled to — for a figure above the fold. */
  immediate?: boolean
}

const numberFormat = (decimals: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

/** Writes a data-count element's final value, for the reduced-motion path. */
function writeFinal(el: HTMLElement) {
  const decimals = Number(el.dataset.decimals ?? 0)
  el.textContent = `${el.dataset.prefix ?? ''}${numberFormat(decimals).format(
    Number(el.dataset.count),
  )}${el.dataset.suffix ?? ''}`
}

/* Animates an element's text from one number to another, right now — no
   ScrollTrigger. For a figure that changes in response to input, where the
   jump between two values is the thing worth showing. */
export function animateNumber(
  el: HTMLElement,
  from: number,
  to: number,
  format: (value: number) => string,
  duration = DURATION.fast,
) {
  if (skipMotion() || from === to) {
    el.textContent = format(to)
    return
  }

  const counter = { n: from }

  return gsap.to(counter, {
    n: to,
    duration,
    ease: EASE,
    onUpdate: () => {
      el.textContent = format(counter.n)
    },
  })
}

/** Runs a statistic up to its value when it scrolls into view, once. */
export function countUp(
  el: HTMLElement,
  value: number,
  options: CountUpOptions = {},
) {
  const {
    duration = DURATION.slow,
    decimals = 0,
    prefix = '',
    suffix = '',
    start = 'top 85%',
    immediate = false,
  } = options

  const format = numberFormat(decimals)
  const write = (n: number) =>
    (el.textContent = `${prefix}${format.format(n)}${suffix}`)

  if (skipMotion()) {
    write(value)
    return
  }

  const counter = { n: 0 }

  return gsap.to(counter, {
    n: value,
    duration,
    ease: EASE,
    onUpdate: () => write(counter.n),
    scrollTrigger: immediate ? undefined : { trigger: el, start, once: true },
  })
}

/* Draws the connecting line of a numbered process as it is scrolled past,
   lighting each step — and its painted numeral — as the line reaches it. */
export function stepProgress(track: Element, axis: 'x' | 'y' = 'x') {
  const line = track.querySelector<HTMLElement>('[data-progress-line]')
  const steps = track.querySelectorAll('[data-progress-step]')
  const numerals = track.querySelectorAll('[data-progress-num]')

  if (skipMotion()) return

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: track,
      start: 'top 72%',
      end: 'bottom 65%',
      scrub: 0.6,
    },
  })

  if (line) {
    const from = axis === 'x' ? { scaleX: 0 } : { scaleY: 0 }
    const to = axis === 'x' ? { scaleX: 1 } : { scaleY: 1 }

    timeline.fromTo(
      line,
      from,
      {
        ...to,
        ease: 'none',
        transformOrigin: axis === 'x' ? 'left center' : 'center top',
      },
      0,
    )
  }

  if (steps.length) {
    timeline.fromTo(
      steps,
      { opacity: 0.25 },
      { opacity: 1, ease: 'none', stagger: 0.5 },
      0,
    )
  }

  if (numerals.length) {
    timeline.fromTo(
      numerals,
      { color: 'rgb(200 245 66 / 0.25)' },
      { color: 'rgb(200 245 66)', ease: 'none', stagger: 0.5 },
      0,
    )
  }

  return timeline
}

/** After a route swap, ScrollTrigger's cached positions are stale. */
export function refreshScrollTriggers() {
  ScrollTrigger.refresh()
}

/* ---------- premium reveals ---------- */

type SplitLinesOptions = {
  delay?: number
  duration?: number
  stagger?: number
  /** Wait for the element to be scrolled to. Off for above-the-fold copy. */
  scroll?: boolean
  start?: string
}

/* A headline that rises line by line from behind its own mask. SplitText
   re-splits on resize and on font load (`autoSplit`), so a reflow never
   leaves a line stranded mid-tween, and `aria: 'auto'` keeps the original
   sentence on the element for screen readers rather than a pile of divs.

   Returns its own teardown: SplitText rewrites the DOM, and a gsap.context
   revert only undoes tweens, not the markup. Callers hand this back from
   the setup callback and useGsapContext calls it. */
export function splitLinesReveal(
  el: HTMLElement,
  options: SplitLinesOptions = {},
) {
  if (skipMotion()) return

  const {
    delay = 0,
    duration = DURATION.base,
    stagger = 0.1,
    scroll = true,
    start = 'top 84%',
  } = options

  /* Above the fold the rise plays at once; otherwise a single trigger,
     made once, plays whichever build is current. Either way the build is
     returned so a re-split carries the playhead across. */
  let played = !scroll
  let current: gsap.core.Timeline | undefined

  const build = (lines: Element[]) => {
    const timeline = gsap.timeline({
      paused: scroll,
      delay: scroll ? 0 : delay + entryDelay(),
    })
    timeline.fromTo(
      lines,
      { yPercent: LINE_RISE },
      { yPercent: 0, duration, stagger, ease: EASE },
    )
    if (scroll && played) timeline.play()
    current = timeline
    return timeline
  }

  if (scroll) {
    ScrollTrigger.create({
      trigger: el,
      start,
      once: true,
      onEnter: () => {
        played = true
        if (current) playAfter(current, delay + entryDelay())
      },
    })
  }

  const split = SplitText.create(el, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'split-line',
    autoSplit: true,
    aria: 'auto',
    onSplit: (self) => build(self.lines),
  })

  return () => split.revert()
}

type ParallaxOptions = {
  /** How far the layer drifts, as a percentage of its own height. */
  amount?: number
  trigger?: Element
  start?: string
  end?: string
}

/* One layer, moving slowly against the scroll. The element must be taller
   than its frame — see the hero, where the plate is inset by -8% — or the
   drift exposes an edge. Deliberately a single layer: the law forbids
   parallax confetti, not depth. */
export function parallax(el: Element, options: ParallaxOptions = {}) {
  if (skipMotion()) return

  const { amount = 6, trigger, start = 'top top', end = 'bottom top' } = options

  return gsap.fromTo(
    el,
    { yPercent: -amount / 2 },
    {
      yPercent: amount / 2,
      ease: 'none',
      scrollTrigger: {
        trigger: trigger ?? el,
        start,
        end,
        scrub: 0.8,
      },
    },
  )
}

type ImageRevealOptions = {
  trigger?: Element
  delay?: number
  duration?: number
  start?: string
  /** Which edge the frame opens from. */
  from?: 'left' | 'right' | 'bottom'
}

/* A photograph arriving: the frame wipes open while the picture inside
   settles from an overscale, like a shutter drawn back from a finished car.
   The clip runs on the frame and the scale on the image inside it, so the
   two never fight for one element's transform. */
export function imageReveal(frame: Element, options: ImageRevealOptions = {}) {
  if (skipMotion()) return

  const {
    trigger,
    delay = 0,
    duration = DURATION.slow,
    start = 'top 78%',
    from = 'bottom',
  } = options

  const closed =
    from === 'left'
      ? 'inset(0% 100% 0% 0%)'
      : from === 'right'
        ? 'inset(0% 0% 0% 100%)'
        : 'inset(100% 0% 0% 0%)'

  const image = frame.querySelector('img, video, [data-reveal-media]')

  const timeline = gsap.timeline({
    paused: true,
    defaults: { ease: EASE },
  })

  timeline.fromTo(
    frame,
    { clipPath: closed },
    {
      clipPath: 'inset(0% 0% 0% 0%)',
      duration,
      ease: EASE_INOUT,
      clearProps: 'clipPath',
    },
    0,
  )

  if (image) {
    timeline.from(
      image,
      { scale: 1.18, duration: duration * 1.25, clearProps: 'transform' },
      0,
    )
  }

  ScrollTrigger.create({
    trigger: trigger ?? frame,
    start,
    once: true,
    onEnter: () => playAfter(timeline, delay + entryDelay()),
  })

  return timeline
}

/* Magnetic pull on a primary action: the button leans a few pixels toward
   the cursor and springs home when it leaves. Fine pointers only — a thumb
   cannot hover — and quiet: the pull is capped well under the button's own
   size, so it reads as weight, not as a chase. */
export function magnetic(el: HTMLElement, strength = 0.32) {
  if (skipMotion()) return
  if (!hasFinePointer()) return

  const xTo = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3.out' })
  const yTo = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' })

  const onMove = (event: PointerEvent) => {
    const rect = el.getBoundingClientRect()
    xTo((event.clientX - rect.left - rect.width / 2) * strength)
    yTo((event.clientY - rect.top - rect.height / 2) * strength)
  }

  const onLeave = () => {
    xTo(0)
    yTo(0)
  }

  el.addEventListener('pointermove', onMove)
  el.addEventListener('pointerleave', onLeave)

  return () => {
    el.removeEventListener('pointermove', onMove)
    el.removeEventListener('pointerleave', onLeave)
  }
}

/** Makes every `[data-magnetic]` inside a scope magnetic; returns one teardown. */
export function magnetize(scope: Element, strength?: number) {
  const teardowns = Array.from(
    scope.querySelectorAll<HTMLElement>('[data-magnetic]'),
  ).map((el) => magnetic(el, strength))

  return () => teardowns.forEach((teardown) => teardown?.())
}

/* Feeds the `card-glow` utility: writes the pointer's position into --mx /
   --my so the CSS spotlight can follow it. All the animation lives in CSS;
   this is only the wiring, so there is nothing to kill but the listener. */
export function pointerGlow(el: HTMLElement) {
  if (!hasFinePointer()) return

  const onMove = (event: PointerEvent) => {
    const rect = el.getBoundingClientRect()
    el.style.setProperty('--mx', `${event.clientX - rect.left}px`)
    el.style.setProperty('--my', `${event.clientY - rect.top}px`)
  }

  el.addEventListener('pointermove', onMove)
  return () => el.removeEventListener('pointermove', onMove)
}

/* Ghost lettering drifting against the scroll — the oversized marque behind
   a band. Scrubbed, not timed, so it moves exactly as fast as the visitor
   does and stops when they do. */
export function driftX(el: Element, amount = 12, trigger?: Element) {
  if (skipMotion()) return

  return gsap.fromTo(
    el,
    { xPercent: amount / 2 },
    {
      xPercent: -amount / 2,
      ease: 'none',
      scrollTrigger: {
        trigger: trigger ?? el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
      },
    },
  )
}

type RiseOptions = {
  /** How far below its resting place the element starts, in percent. */
  amount?: number
  trigger?: Element
  start?: string
  end?: string
}

/* Lettering rising out of the floor — the footer's marque, embossed across
   the foot of the page, climbs up into its clip as the visitor arrives at
   it. Scrubbed, like every other piece of ghost architecture. */
export function riseIn(el: Element, options: RiseOptions = {}) {
  if (skipMotion()) return

  const {
    amount = 60,
    trigger,
    start = 'top bottom',
    end = 'bottom bottom',
  } = options

  return gsap.fromTo(
    el,
    { yPercent: amount },
    {
      yPercent: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: trigger ?? el,
        start,
        end,
        scrub: 0.8,
      },
    },
  )
}

/* A dialog arriving. Enter-only, for the same reason page transitions are:
   an exit tween holds a closed panel on screen after the user has dismissed
   it. The scale is 1.5% — a settle, not a pop. */
export function revealDialog(panel: Element, backdrop?: Element | null) {
  if (skipMotion()) return

  const timeline = gsap.timeline({ defaults: { ease: EASE } })

  if (backdrop) {
    timeline.from(backdrop, { opacity: 0, duration: DURATION.fast }, 0)
  }

  timeline.from(
    panel,
    { opacity: 0, y: 26, scale: 0.985, duration: DURATION.fast },
    0,
  )

  return timeline
}

/* ---------- the chrome ---------- */

/* The bar arriving on first load: mark, links, actions, in reading order,
   each dropping the last few pixels into place. Once per session — the
   shell persists across routes, so this never replays. */
export function navIntro(bar: Element) {
  if (skipMotion() || !bar.children.length) return

  return gsap.from(bar.children, {
    opacity: 0,
    y: -14,
    duration: DURATION.base,
    stagger: 0.1,
    delay: 0.15,
    ease: EASE,
    clearProps: 'transform',
  })
}

/* The bar getting out of the way. Scrolling down through the page, past the
   first few hundred pixels, tucks it above the viewport so the photographs
   get the whole screen; the first scroll back up brings it straight down.
   Driven by `top`, not a transform: the drawer is position:fixed inside it,
   and a transformed ancestor would pin the drawer to the bar instead of
   the viewport. `locked` reports whether a panel is open, in which case the
   bar stays where the pointer is. */
export function navAutoHide(header: HTMLElement, locked: () => boolean) {
  if (skipMotion()) return

  const height = header.offsetHeight || 84
  let hidden = false

  const move = (top: number) =>
    gsap.to(header, {
      top,
      duration: 0.5,
      ease: EASE_INOUT,
      overwrite: 'auto',
    })

  const show = () => {
    if (!hidden) return
    hidden = false
    move(0)
  }

  const hide = () => {
    if (hidden) return
    hidden = true
    move(-height - 8)
  }

  return ScrollTrigger.create({
    start: 0,
    end: 'max',
    onUpdate: (self) => {
      if (locked()) return show()
      if (self.direction === 1 && self.scroll() > 320) hide()
      else if (self.direction === -1) show()
    },
  })
}

/* A lime hairline along the top edge that grows with the page — the one
   reading of "how far" a page this long owes its visitor. Scrubbed against
   the whole document, so a route change only needs a refresh. */
export function scrollProgress(bar: Element) {
  return gsap.fromTo(
    bar,
    { scaleX: 0 },
    {
      scaleX: 1,
      ease: 'none',
      transformOrigin: 'left center',
      scrollTrigger: {
        start: 0,
        end: 'max',
        scrub: 0.3,
      },
    },
  )
}

/* The curtain between pages. It is already down when this is called — the
   new page mounted underneath it — and lifts from the top, its lime edge
   leading, while the page beneath rises to meet it. Enter-only: the
   outgoing page was never held, so navigation is never delayed. */
export const CURTAIN = {
  /** How long the curtain takes to clear the screen. */
  duration: 0.8,
  /** How long content above the fold should wait before arriving. */
  holdMs: 420,
} as const

export function pageCurtain(curtain: Element, page: Element) {
  const timeline = gsap.timeline()

  timeline
    .set(curtain, { yPercent: 0, visibility: 'visible' })
    .to(curtain, {
      yPercent: 100,
      duration: CURTAIN.duration,
      ease: 'power3.inOut',
    })
    .set(curtain, { visibility: 'hidden' })

  timeline.fromTo(
    page,
    { opacity: 0, y: 28 },
    {
      opacity: 1,
      y: 0,
      duration: DURATION.base,
      ease: EASE,
      clearProps: 'transform',
    },
    0.2,
  )

  return timeline
}
